<template>
  <div class="space-y-6 max-w-4xl">
    <Head title="Editar Tasa de Cambio" />

    <!-- Migas de pan / Cabecera -->
    <div class="flex items-center justify-between">
      <div class="flex items-center gap-2 text-xs text-gray-500">
        <Link class="text-orange-600 hover:text-orange-700 font-semibold" href="/taxes">Tasas de cambio</Link>
        <span class="text-gray-300">/</span>
        <span class="text-gray-700 font-medium">Editar #{{ taxe.id }}</span>
      </div>
      <Link href="/taxes" class="text-xs text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1.5">
        <i class="fas fa-arrow-left text-[10px]" /> Volver al listado
      </Link>
    </div>

    <!-- Formulario principal -->
    <div class="bg-white rounded-2xl border border-gray-100 shadow-2xs overflow-hidden">
      <div class="p-6 border-b border-gray-100 flex items-center justify-between">
        <div>
          <h2 class="text-sm font-bold text-gray-900">Editar Tasa de Cambio #{{ taxe.id }}</h2>
          <p class="text-xs text-gray-500 mt-0.5">Registrada el {{ formatDateTime(taxe.created_at) }}</p>
        </div>

        <button 
          v-if="bcvData?.rate"
          type="button" 
          class="h-7 px-3 bg-orange-50 hover:bg-orange-100 text-orange-600 rounded-lg text-xs font-semibold transition-colors flex items-center gap-1" 
          @click="useBcvValue"
        >
          <i class="fas fa-magic text-[10px]" /> Cargar valor BCV ({{ formatPrice(bcvData.rate) }})
        </button>
      </div>

      <form @submit.prevent="update">
        <div class="p-6 space-y-5">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-5">
            <!-- Campo Tasa USD / Bs -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Tasa USD (Bolívares) *</label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">Bs.</span>
                <input 
                  v-model.number="form.change" 
                  type="number" 
                  step="0.0001"
                  min="0.0001"
                  required 
                  placeholder="Ej: 880.50"
                  class="w-full text-xs rounded-xl border-gray-200 pl-9 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-bold text-gray-900 transition-colors"
                  :class="{ 'border-red-300': form.errors.change }"
                />
              </div>
              <p v-if="form.errors.change" class="text-[10px] text-red-500 mt-1">{{ form.errors.change }}</p>
            </div>

            <!-- Campo Tasa COP -->
            <div>
              <label class="block text-xs font-semibold text-gray-700 mb-1.5">Tasa Pesos Colombianos (COP) <span class="font-normal text-gray-400">(opcional)</span></label>
              <div class="relative">
                <span class="absolute left-3 top-2.5 text-xs text-gray-400 font-semibold">COP</span>
                <input 
                  v-model.number="form.cop_change" 
                  type="number" 
                  step="0.01"
                  min="0" 
                  placeholder="Ej: 4150.00"
                  class="w-full text-xs rounded-xl border-gray-200 pl-11 pr-3 py-2 bg-gray-50/50 focus:bg-white focus:ring-orange-500 focus:border-orange-500 font-semibold text-gray-900 transition-colors"
                />
              </div>
            </div>
          </div>
        </div>

        <div class="px-6 py-4 bg-gray-50/70 border-t border-gray-100 flex items-center justify-between gap-3">
          <button 
            type="button" 
            class="text-xs font-medium text-red-600 hover:text-red-700 transition-colors flex items-center gap-1"
            @click="destroy"
          >
            <i class="fas fa-trash text-xs" /> Eliminar
          </button>

          <div class="flex items-center gap-2">
            <Link href="/taxes" class="px-4 py-2 text-xs font-medium text-gray-600 hover:text-gray-900 transition-colors">
              Cancelar
            </Link>
            <button 
              type="submit" 
              :disabled="form.processing || !form.change"
              class="h-9 px-5 bg-orange-500 hover:bg-orange-600 text-white text-xs font-semibold rounded-xl shadow-xs active:scale-95 transition-all flex items-center gap-1.5 disabled:opacity-50"
            >
              <i v-if="form.processing" class="fas fa-spinner fa-spin text-xs" />
              <i v-else class="fas fa-save text-xs" />
              <span>{{ form.processing ? 'Guardando...' : 'Actualizar Tasa' }}</span>
            </button>
          </div>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
import { Head, Link } from '@inertiajs/inertia-vue3'
import Layout from '@/Shared/Layout'

export default {
  layout: Layout,

  props: {
    taxe: Object,
    accounts: Array,
    bcvData: Object,
  },

  data() {
    return {
      form: this.$inertia.form({
        change: Number(this.taxe.change || this.taxe.rate),
        cop_change: this.taxe.cop_change ? Number(this.taxe.cop_change) : '',
        account_id: this.taxe.account_id || '',
      }),
    }
  },

  methods: {
    formatPrice(val) {
      if (val === null || val === undefined || isNaN(val)) return '0.00'
      return Number(val).toLocaleString('es-VE', { minimumFractionDigits: 2, maximumFractionDigits: 4 })
    },

    formatDateTime(dateStr) {
      if (!dateStr) return '-'
      try {
        const d = new Date(dateStr)
        return d.toLocaleDateString('es-VE', { 
          day: '2-digit', 
          month: '2-digit', 
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        })
      } catch (e) {
        return dateStr
      }
    },

    useBcvValue() {
      if (this.bcvData?.rate) {
        this.form.change = Number(this.bcvData.rate)
      }
    },

    update() {
      this.form.put(`/taxes/${this.taxe.id}`)
    },

    destroy() {
      if (confirm('¿Estás seguro que deseas eliminar esta tasa de cambio?')) {
        this.$inertia.delete(`/taxes/${this.taxe.id}`)
      }
    },
  },
}
</script>